/**
 * Local dev: simulate RBAC personas without dedicated DB users.
 *
 * Usage (Vite dev / import.meta.env.DEV only — no-op in production builds):
 * - One-shot from URL: append `?devPersona=<id>` then reload (value is stored in localStorage).
 * - Clear: `?devPersona=off` or remove key `payhelm_dev_permission_persona` from localStorage and reload.
 * - Or set directly: `localStorage.setItem('payhelm_dev_permission_persona', '<id>'); location.reload()`
 *
 * IMPORTANT: This replaces **Redux** `permissions` and optionally **admin** after login so hooks
 * (useCanShip, useRiskCapabilities) match the persona. **APIs still use your real cookie/session** —
 * e.g. an admin may still get unsanitized search responses from papaya unless you also use a
 * non-admin test account or server-side strict RBAC.
 */
import { PERMISSIONS } from 'sharedComponents/Router.permissionRoute'

export const DEV_PERMISSION_PERSONA_STORAGE_KEY = 'payhelm_dev_permission_persona'
export const DEV_PERMISSION_PERSONA_QUERY = 'devPersona'

export type DevPermissionPersonaId =
  | 'merchant_shipping_purchase'
  | 'merchant_shipping_claims'
  | 'merchant_shipping_view_only'
  | 'merchant_shipping_settings'
  | 'underwriter_risk'

type PersonaDef = {
  permissions: string[]
  /** When true, force `setAdmin(false)` so admin-only UI matches a merchant teammate. */
  simulateNonAdmin: boolean
}

const PERSONAS: Record<DevPermissionPersonaId, PersonaDef> = {
  merchant_shipping_purchase: {
    permissions: [PERMISSIONS.SHIPPING_VIEW, PERMISSIONS.SHIPPING_PURCHASE],
    simulateNonAdmin: true,
  },
  merchant_shipping_claims: {
    permissions: [PERMISSIONS.SHIPPING_VIEW, PERMISSIONS.SHIPPING_CLAIMS],
    simulateNonAdmin: true,
  },
  merchant_shipping_view_only: {
    permissions: [PERMISSIONS.SHIPPING_VIEW],
    simulateNonAdmin: true,
  },
  merchant_shipping_settings: {
    permissions: [PERMISSIONS.SHIPPING_VIEW, PERMISSIONS.SHIPPING_SETTINGS],
    simulateNonAdmin: true,
  },
  underwriter_risk: {
    permissions: [
      PERMISSIONS.SHIPPING_VIEW,
      PERMISSIONS.RISK_VIEW,
      PERMISSIONS.RISK_ORDER_DETAIL,
      PERMISSIONS.RISK_MERCHANT_PROFILE,
    ],
    simulateNonAdmin: true,
  },
}

export function isDevPermissionPersonaEnabled(): boolean {
  return import.meta.env.DEV === true
}

/** Read `?devPersona=` and sync to localStorage (or clear when `off`). Call on boot and popstate. */
export function captureDevPersonaFromUrl(): void {
  if (!isDevPermissionPersonaEnabled()) return
  if (typeof window === 'undefined') return
  const raw = new URLSearchParams(window.location.search).get(DEV_PERMISSION_PERSONA_QUERY)
  if (raw === null) return
  const id = raw.trim()
  if (id === '' || id === 'off') {
    window.localStorage.removeItem(DEV_PERMISSION_PERSONA_STORAGE_KEY)
    return
  }
  if (id in PERSONAS) {
    window.localStorage.setItem(DEV_PERMISSION_PERSONA_STORAGE_KEY, id)
    // eslint-disable-next-line no-console
    console.info(
      `[PayHelm dev] Permission persona "${id}" saved (Redux syncs on load; reload if hooks still look stale).`,
    )
  } else {
    // eslint-disable-next-line no-console
    console.warn(
      `[PayHelm dev] Unknown devPersona "${id}". Valid: ${Object.keys(PERSONAS).join(', ')}, off`,
    )
  }
}

export function getActiveDevPersonaId(): DevPermissionPersonaId | null {
  if (!isDevPermissionPersonaEnabled()) return null
  if (typeof window === 'undefined') return null
  const id = window.localStorage.getItem(DEV_PERMISSION_PERSONA_STORAGE_KEY)?.trim() || ''
  if (!id || !(id in PERSONAS)) return null
  return id as DevPermissionPersonaId
}

export function listDevPermissionPersonas(): readonly DevPermissionPersonaId[] {
  return Object.keys(PERSONAS) as DevPermissionPersonaId[]
}

/**
 * Apply stored persona to `/permissions/me` results. When no persona, returns inputs unchanged.
 */
export function applyDevPersonaToSessionPermissions(
  serverPermissions: string[],
  serverAdmin: boolean,
): { permissions: string[]; admin: boolean; active: boolean } {
  if (!isDevPermissionPersonaEnabled()) {
    return { permissions: serverPermissions, admin: serverAdmin, active: false }
  }
  const id = getActiveDevPersonaId()
  if (!id) {
    return { permissions: serverPermissions, admin: serverAdmin, active: false }
  }
  const def = PERSONAS[id]
  const permissions = [...def.permissions]
  const admin = def.simulateNonAdmin ? false : serverAdmin
  // eslint-disable-next-line no-console
  console.info(
    `[PayHelm dev] Permission persona active: "${id}" → permissions [${permissions.join(', ')}], admin=${admin} (API session unchanged).`,
  )
  return { permissions, admin, active: true }
}

/**
 * When a dev persona is stored, return its permission list and simulated admin flag.
 * Risk/shipping hooks use this so an admin cookie session still matches the persona
 * without requiring a full re-login after `?devPersona=` changes.
 */
export function getDevPersonaSessionOverride(): {
  permissions: string[]
  admin: boolean
} | null {
  if (!isDevPermissionPersonaEnabled()) return null
  const id = getActiveDevPersonaId()
  if (!id) return null
  const def = PERSONAS[id]
  return {
    permissions: [...def.permissions],
    admin: def.simulateNonAdmin ? false : true,
  }
}

export function installDevPersonaUrlCapture(onPersonaChanged?: () => void): void {
  if (!isDevPermissionPersonaEnabled()) return
  if (typeof window === 'undefined') return
  const run = () => {
    captureDevPersonaFromUrl()
    onPersonaChanged?.()
  }
  run()
  window.addEventListener('popstate', run)
}
